import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper';

import Header from '../components/Header';
import { hotelAssets } from '../assets/hotelAssets';


Modal.setAppElement('#root');

const Gallery = () => {
  const gallery = hotelAssets.gallery;
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState('center');
  const [modalImages, setModalImages] = useState([]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setHeaderScrolled(true);
      } else {
        setHeaderScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
  };

  const openModal = (images) => {
    setModalImages(images);
    setIsModalOpen(true);
    setModalPosition('bottom');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImages([]);
  };

  return (
    <div>
      <Header />

      <div className={`max-w-screen-lg mx-auto ${headerScrolled ? 'pt-40' : 'pt-32'} py-8 text-center`}>
        <h1 className="text-4xl font-bold mb-10">Gallery</h1>

        {/* Toggle Buttons */}
        <div className="flex flex-wrap justify-center space-x-1 mb-6">
          <ToggleButton
            text="All"
            isSelected={selectedTab === 'all'}
            onClick={() => handleTabClick('all')}
          />
          <ToggleButton
            text="Property"
            isSelected={selectedTab === 'property'}
            onClick={() => handleTabClick('property')}
          />
          
          <ToggleButton
            text="Rooms"
            isSelected={selectedTab === 'rooms'}
            onClick={() => handleTabClick('rooms')}
          />
         
        </div>

        {/* Gallery Section */}
        <div className="flex flex-col gap-8 items-center">
          {selectedTab === 'all' && (
            <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow duration-300 w-full">
              <div onClick={() => openModal(gallery.all)} className="cursor-pointer">
                <div className="relative">
                  <img src={gallery.covers.all} alt="All Images" className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg" />
                  <div className="absolute bottom-0 left-0 p-4 text-white">
                    <p className="text-lg font-bold">View All Images</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {selectedTab === 'property' && (
            <PropertySection openModal={() => openModal(gallery.property)} coverImage={gallery.covers.property} />
          )}
          {selectedTab === 'rooms' && (
            <GallerySection
              openModal={(roomType) => {
                if (roomType === 'suit') {
                  openModal(gallery.rooms.suit);
                } else if (roomType === 'queen') {
                  openModal(gallery.rooms.queen);
                } else if (roomType === 'superior') {
                  openModal(gallery.rooms.superior);
                }
              }}
              coverImages={gallery.covers}
              headerScrolled={headerScrolled}
            />
          )}
          {selectedTab === 'washroom' && (
            <WashroomSection openModal={() => openModal(gallery.washroom)} coverImage={gallery.covers.washroom} />
          )}
          
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Image Modal"
        className={`fixed inset-0 flex items-start justify-center p-6 bg-gray-1100 bg-opacity-85 ${modalPosition === 'bottom' ? 'bottom-0' : ''} mt-20`}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
      >
        <div className="bg-white p-4 rounded-lg max-w-3xl w-full">
          <button onClick={closeModal} className="text-black text-xl font-bold mb-4">
            Close
          </button>
          <Swiper
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            navigation={true}
            pagination={{ clickable: true }}
            modules={[Navigation, Pagination]}
            className="mySwiper"
          >
            {modalImages.map((img, index) => (
              <SwiperSlide key={index}>
                <img src={img} alt={`Room gallery ${index + 1}`} className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg" />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Modal>
    </div>
  );
};

const ToggleButton = ({ text, isSelected, onClick }) => (
  <button
    className={`text-lg font-bold py-2 px-4 flex items-center justify-center hover:text-red-400 transition-colors ${isSelected ? 'text-red-400' : 'text-gray-700'
      }`}
    onClick={onClick}
  >
    {text}
  </button>
);

const PropertySection = ({ openModal, coverImage }) => (
  <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow duration-300 w-full">
    <div onClick={openModal} className="cursor-pointer">
      <div className="relative">
        <img src={coverImage} alt="Property Images" className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg" />
        <div className="absolute bottom-0 left-0 p-4 text-white">
          <p className="text-lg font-bold">View our Property Images</p>
        </div>
      </div>
    </div>
  </div>
);

const WashroomSection = ({ openModal, coverImage }) => (
  <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow duration-300 w-full">
    <div onClick={openModal} className="cursor-pointer">
      <div className="relative">
        <img src={coverImage} alt="Washroom Images" className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg" />
        <div className="absolute bottom-0 left-0 p-4 text-white">
          <p className="text-lg font-bold">View Washroom Images</p>
        </div>
      </div>
    </div>
  </div>
);



const GallerySection = ({ openModal, coverImages }) => (
  <div className="p-4 border rounded-lg hover:shadow-lg transition-shadow duration-300 w-full">
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="w-full lg:w-1/2">
        <div className="relative">
          <img
            onClick={() => openModal('suit')}
            src={coverImages.suit}
            alt={`Suit Room`}
            className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg cursor-pointer"
          />
          <div className="absolute bottom-0 left-0 p-4 text-red-400">
            <p className="text-lg font-bold">Suit Room</p>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-1/2">
        <div className="relative">
          <img
            onClick={() => openModal('queen')}
            src={coverImages.queen}
            alt={`Queen Room`}
            className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg cursor-pointer"
          />
          <div className="absolute bottom-0 left-0 p-4 text-red-400">
            <p className="text-lg font-bold">Queen Room</p>
          </div>
        </div>
      </div>
      <div className="w-full lg:w-1/2">
        <div className="relative">
          <img
            onClick={() => openModal('superior')}
            src={coverImages.superior}
            alt={`Superior Room`}
            className="object-cover w-full h-64 md:h-80 lg:h-96 rounded-lg cursor-pointer"
          />
          <div className="absolute bottom-0 left-0 p-4 text-red-400">
            <p className="text-lg font-bold">Superior Room</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);



export default Gallery;
